import React, { useState, useEffect, useMemo } from 'react';
import { 
    Target, Calendar, 
    ArrowRight, Loader2, Search, 
    AlertTriangle, Plus, Users, MapPin, Timer
} from 'lucide-react';
import { motion } from 'framer-motion';
import { getFirestore } from 'firebase/firestore';

import { SimulatedCampaignSummary } from './SimulatedCampaignSummary';

import { encryptedFetch as fetch } from '../security';

// Initialize DB
const db = getFirestore();

// ====================================================================================
// COMPONENT: MINI GRADE BADGE
// ====================================================================================
const MiniGrade = ({ grade }) => {
    const config = {
        'A+': { text: 'text-teal-300', bg: 'bg-teal-500/20', border: 'border-teal-500/30' },
        'A':  { text: 'text-emerald-300', bg: 'bg-emerald-500/20', border: 'border-emerald-500/30' },
        'B':  { text: 'text-blue-300', bg: 'bg-blue-500/20', border: 'border-blue-500/30' },
        'C':  { text: 'text-yellow-300', bg: 'bg-yellow-500/20', border: 'border-yellow-500/30' },
        'D':  { text: 'text-orange-300', bg: 'bg-orange-500/20', border: 'border-orange-500/30' },
        'F':  { text: 'text-red-300', bg: 'bg-red-500/20', border: 'border-red-500/30' },
    }[grade] || { text: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/20' };

    return (
        <div className={`flex flex-col items-center justify-center w-10 h-10 rounded-lg border ${config.border} ${config.bg} backdrop-blur-md shadow-inner`}>
            <span className={`text-lg font-bold ${config.text} leading-none font-sans`}>{grade}</span>
        </div>
    );
};

// ====================================================================================
// HELPER: DATE PARSING
// ====================================================================================
const parseDate = (timestamp) => {
    if (!timestamp) return new Date(0); // Return epoch if null so it sorts to bottom
    if (timestamp.toDate) return timestamp.toDate(); // Firestore
    if (typeof timestamp === 'string') return new Date(timestamp); // ISO String
    return new Date(0);
};

// ====================================================================================
// MAIN COMPONENT
// ====================================================================================

const CampaignGallery = ({ campaignId, onViewSimulation, onStartCampaign }) => {
    const [simulations, setSimulations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSimulation, setSelectedSimulation] = useState(null);

    // Fetch Logic
    useEffect(() => {
        if (campaignId || (selectedSimulation === null)) {
        const fetchSimulations = async () => {
            try {
                const response = await fetch("https://us-central1-knoesis-dante.cloudfunctions.net/getSimulatedCampaign?campaignID=" + campaignId);
                const data = await response.json();
                setSimulations(Array.isArray(data) ? data : []);
                setLoading(false);
            } catch (err) {
                console.error("Error loading campaigns:", err);
                setError("Failed to load simulation library.");
                setLoading(false);
            }
        };

        fetchSimulations();
    }
    }, [campaignId, selectedSimulation]);

    // Sorting & Filtering Logic
    const processedSimulations = useMemo(() => {
        let processed = [...simulations];

        // 1. Sort by Date (Newest First)
        processed.sort((a, b) => {
            const dateA = parseDate(a.analysis_metadata?.timestamp);
            const dateB = parseDate(b.analysis_metadata?.timestamp);
            return dateB - dateA; // Descending order
        });

        // 2. Filter by Search
        if (searchTerm) {
            processed = processed.filter(sim => {
                const name = sim.analysis_metadata?.campaign_name || 'Untitled';
                return name.toLowerCase().includes(searchTerm.toLowerCase());
            });
        }

        return processed;
    }, [simulations, searchTerm]);

    // Navigation Handlers
    const handleCardClick = (simulation) => {
        setSelectedSimulation(simulation);
        if (onViewSimulation) onViewSimulation(simulation);
    };

    const handleBackToGallery = () => {
        setSelectedSimulation(null);
    };

    const handleStartClick = () => {
        if (onStartCampaign) onStartCampaign();
    };

    const formatDateStr = (timestamp) => {
        const dateObj = parseDate(timestamp);
        return dateObj.toLocaleDateString('en-GB', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric' 
        });
    };

    if (loading) {
        return (
            <div className="h-full w-full flex flex-col items-center justify-center text-slate-500 min-h-[50vh] bg-slate-950">
                <Loader2 size={48} className="animate-spin mb-4 text-slate-400" />
                <p className="font-mono text-xs uppercase tracking-widest text-slate-600">Accessing Dante Archives...</p>
            </div>
        );
    }

    // --- VIEW: SIMULATION SUMMARY redirect from calendar ---
    const sim_id = new URL(window.location.href).searchParams.get("sim")
    if (sim_id) {
        const selected = simulations.find((simulation) => simulation.analysis_metadata.session_id === sim_id);
        return (
            <div className="relative bg-slate-950 min-h-screen animate-in fade-in duration-500">
                <SimulatedCampaignSummary 
                    data={selected}  
                />
            </div>
        );
    }

    // --- VIEW: SIMULATION SUMMARY ---

    if (selectedSimulation) {
        return (
            <div className="relative bg-slate-950 min-h-screen animate-in fade-in duration-500">
                <SimulatedCampaignSummary 
                    data={selectedSimulation}
                    onDelete={() => fetch(`https://us-central1-knoesis-dante.cloudfunctions.net/reuseSimulatedCampaign?campaignID=${campaignId}&sessionId=${selectedSimulation.analysis_metadata.session_id}&reuse=false`,{}).then(() => setSelectedSimulation(null))
                    }
                    onBack={handleBackToGallery} 
                />
            </div>
        );
    }

    // --- VIEW: GALLERY GRID ---
    return (
        <div className="bg-slate-950 min-h-screen text-slate-200 p-6 lg:p-12 font-sans relative">
            
            <div className="max-w-7xl mx-auto relative z-10">
                
                {/* --- HEADER SECTION --- */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                    <div>
                        <h1 className="text-3xl font-bold text-white tracking-tight mb-2">
                            MATRIX Simulation Library
                        </h1>
                        <p className="text-slate-400 text-sm max-w-lg leading-relaxed">
                            Access your archive of strategic simulations generated by DANTE. Review projected votes, budget efficiency, and strategic scores.
                        </p>
                    </div>

                    {/* ACTIONS */}
                    <div className="flex flex-col md:flex-row gap-4 w-full md:w-auto items-center">
                        <button
                            onClick={handleStartClick}
                            className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-slate-100 
                                     bg-slate-700 hover:bg-slate-600 
                                     hover:drop-shadow-[0_0_5px_rgba(203,213,225,0.2)] 
                                     active:bg-gradient-to-br active:from-slate-700 active:to-slate-800 
                                     transition duration-300 uppercase tracking-wide border border-transparent whitespace-nowrap"
                        >
                            <Plus size={16} />
                            <span>Start New Campaign</span>
                        </button>

                        <div className="relative group w-full md:w-64">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-500 group-focus-within:text-slate-300 transition-colors">
                                <Search size={16} />
                            </div>
                            <input 
                                type="text" 
                                placeholder="Search campaigns..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-800 text-slate-100 rounded-lg text-sm shadow-sm placeholder-slate-600 
                                         focus:outline-none focus:ring-1 focus:ring-slate-600 focus:border-slate-600 transition-all duration-300
                                         hover:border-slate-700"
                            />
                        </div>
                    </div>
                </div>
                
                {error && (
                    <div className="bg-red-900/20 border border-red-500/20 p-4 rounded-lg flex items-center gap-3 text-red-300 mb-8 backdrop-blur-sm">
                        <AlertTriangle size={20} />
                        <span>{error}</span>
                    </div>
                )}

                {/* --- GRID LAYOUT --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    
                    {/* EMPTY STATE: Only shows if there are absolutely no simulations.
                        Does not sit inside the map to prevent grid disruption.
                    */}
                    {simulations.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            onClick={handleStartClick}
                            className="group h-[260px] rounded-2xl border border-dashed border-slate-700 bg-slate-900/20 hover:bg-slate-900/40 cursor-pointer flex flex-col items-center justify-center text-center transition-all duration-300 hover:border-slate-500"
                        >
                            <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mb-4 group-hover:bg-slate-700 transition-colors shadow-lg">
                                <Plus size={32} className="text-slate-500 group-hover:text-slate-200 transition-colors" />
                            </div>
                            <h3 className="font-bold text-slate-300 text-lg mb-1">New Simulation</h3>
                            <p className="text-xs text-slate-500 px-8">
                                Initialize the matrix.
                            </p>
                        </motion.div>
                    )}

                    {/* CAMPAIGN CARDS */}
                    {processedSimulations.map((sim, index) => {
                        const summary = sim.input_summary || {};
                        const scorecard = sim.performance_dashboard?.campaign_scorecard || {};
                        const optimization = sim.campaign_optimization?.optimal_budget_allocation || {};
                        const metadata = sim.analysis_metadata || {};
                        
                        // Data Parsing
                        const dateStr = formatDateStr(metadata.timestamp);
                        const projectedVotes = optimization.expected_total_votes || 0;
                        const wardCount = summary.ward_count || 0;
                        const daysUntil = summary.days_until_election || 0;

                        return (
                            <motion.div
                                key={sim.id || index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                                onClick={() => handleCardClick(sim)}
                                className="group relative flex flex-col justify-between h-[260px] bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-2xl p-6 cursor-pointer overflow-hidden transition-all duration-500 ease-in-out hover:-translate-y-1 hover:border-slate-600 hover:drop-shadow-[0_0_8px_rgba(203,213,225,0.25)]"
                            >
                                {/* Top Section: Header */}
                                <div>
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500 bg-slate-950/50 px-2 py-1 rounded border border-slate-800">
                                            <Calendar size={10} />
                                            <span>{dateStr}</span>
                                        </div>
                                        <MiniGrade grade={scorecard.overall_grade || '-'} />
                                    </div>

                                    <h3 className="font-bold text-slate-100 text-lg leading-tight line-clamp-2 mb-2 group-hover:text-white transition-colors">
                                        {metadata.campaign_name ?? 'Untitled Campaign'}
                                    </h3>

                                    <div className="flex items-center gap-2 text-xs text-slate-400 group-hover:text-slate-300 transition-colors">
                                        <Target size={12} className="text-slate-500" />
                                        <span className="truncate">{summary.campaign_focus?.replace(/_/g, ' ') || 'General Strategy'}</span>
                                    </div>
                                </div>

                                {/* Divider */}
                                <div className="w-full h-px bg-gradient-to-r from-transparent via-slate-700/50 to-transparent my-2"></div>

                                {/* Bottom Section: KPI Stats */}
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* KPI 1: Budget */}
                                        <div>
                                            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                                                Budget
                                            </div>
                                            <div className="text-emerald-400 font-mono font-bold text-base truncate">
                                                £{(summary.budget || 0).toLocaleString()}
                                            </div>
                                        </div>
                                        
                                        {/* KPI 2: Projected Votes */}
                                        <div className="text-right">
                                            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 flex items-center justify-end gap-1">
                                                <Users size={10} />
                                                Est. Votes
                                            </div>
                                            <div className="text-blue-300 font-mono font-bold text-base truncate">
                                                {Math.round(projectedVotes).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer: Scope Indicators */}
                                    <div className="pt-2 flex justify-between items-center border-t border-slate-800/50 mt-1">
                                        <div className="flex items-center gap-3 text-[10px] text-slate-500 uppercase tracking-wider font-mono">
                                            <div className="flex items-center gap-1" title="Wards Analyzed">
                                                <MapPin size={10} />
                                                <span>{wardCount}</span>
                                            </div>
                                            <div className="flex items-center gap-1" title="Days Until Election">
                                                <Timer size={10} />
                                                <span>{daysUntil}d</span>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-1 text-xs font-bold text-slate-400 group-hover:text-white transition-colors duration-300">
                                            <span>VIEW</span>
                                            <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform duration-300" />
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Hover Gradient */}
                                <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-500"></div>
                            </motion.div>
                        );
                    })}
                </div>
                
                {/* Search Empty State */}
                {processedSimulations.length === 0 && simulations.length > 0 && (
                    <div className="text-center py-20">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-900 border border-slate-800 mb-4">
                            <Search size={20} className="text-slate-500" />
                        </div>
                        <h3 className="text-slate-300 font-medium">No campaigns found</h3>
                        <p className="text-slate-500 text-sm mt-1">Try adjusting your search for "{searchTerm}"</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CampaignGallery;